import { faList, faWarning } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Tooltip } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { Chip, Popover, Typography } from '@vapor/material'
import { useMemo, useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { EmailState, EmailStateTransaction } from 'src/api/api.generated'
import { HicDataGridPremium } from 'src/app/components/hic-vapor/datagrid/datagrid'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import MailStateTimeline from './mail-state-timeline'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { FormLayoutAppearance } from 'src/app/layout/form-layout/form-layout.types'
import { faEye } from '@fortawesome/pro-regular-svg-icons'
import FormLayoutDrawer from 'src/app/layout/form-layout/form-layout-drawer'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import ChatDrawer from './chat-drawer'
import { Action } from 'src/app/components/data-grid-action-cell-item'

type MailLogDrawerProps = {
  appearance: FormLayoutAppearance
  rows: (MailLogRow | ThreadRow)[]
  loading: boolean
  title?: string
  toolbar: React.ReactNode
}

export type ThreadRow = {
  id: string
  date: Date
  description: string
  receiver: string
  archived: boolean
  type: 'THREAD'
}

export type MailLogRow = {
  id: string
  date: Date
  description: string
  receiver: string
  state: EmailState
  template: string
  timeline: EmailStateTransaction[]
  type: 'MAIL'
}

function isRowEmail(row: MailLogRow | ThreadRow): row is MailLogRow {
  return row.type === 'MAIL'
}

function isRowThread(row: MailLogRow | ThreadRow): row is ThreadRow {
  return row.type === 'THREAD'
}

export default function MailLog(props: MailLogDrawerProps) {
  const { rows, loading, title, appearance, toolbar } = props
  const { t } = useTranslation()

  //#region dialog
  const [mailPreview, setMailPreview] = useState<string>()
  const openMailPreview = useMemo(() => mailPreview !== undefined, [mailPreview])
  const closeMailPreview = () => setMailPreview(undefined)
  //#endregion dialog

  //#region popover
  const [showTimeline, setShowTimeline] = useState<EmailStateTransaction[]>()
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)

  const handleShowStatesTimeline = (event: React.MouseEvent<HTMLDivElement>, timeline: EmailStateTransaction[]) => {
    setAnchorEl(event.currentTarget)
    setShowTimeline(timeline)
  }
  //#endregion popover

  const [threadId, setThreadId] = useState<string | null>(null)
  const closeChat = () => {
    setThreadId(null)
  }
  const openChat = (threadId: string) => {
    setThreadId(threadId)
  }

  const getStateText = (state: EmailState) => {
    switch (state) {
      case EmailState.BOUNCE:
        return t('pms.mail_log_drawer.state.bounce')
      case EmailState.CLICK:
        return t('pms.mail_log_drawer.state.click')
      case EmailState.DELIVERY:
        return t('pms.mail_log_drawer.state.delivery')
      case EmailState.ERROR:
        return t('pms.mail_log_drawer.state.error')
      case EmailState.OPEN:
        return t('pms.mail_log_drawer.state.open')
      case EmailState.SEND:
        return t('pms.mail_log_drawer.state.send')
    }
  }

  const getStateIsError = (state: EmailState) => {
    return state === EmailState.BOUNCE || state === EmailState.ERROR
  }

  const columns = useColumns(t, getStateText, (template) => setMailPreview(template), handleShowStatesTimeline, openChat)

  return (
    <>
      <FormLayout title={title ?? t('pms.mail_log_drawer.title')} appearance={{ ...appearance }} toolbar={toolbar}>
        <HicDataGridPremium loading={loading} isRowSelectable={() => false} hideFooter={true} columns={columns} rows={rows} disableColumnMenu autoHeight />
      </FormLayout>

      <FormLayoutDrawer
        title={t('pms.mail_log_drawer.mail_preview_dialog.title')}
        open={openMailPreview}
        onClose={closeMailPreview}
        tight
        toolbar={
          <FormLayoutDefaultToolbar
            cancelButton={{
              text: t('commons.close'),
              clickHandler: closeMailPreview,
            }}
          />
        }
      >
        <iframe srcDoc={mailPreview} style={{ height: '100%', width: '100%' }}></iframe>
      </FormLayoutDrawer>
      {threadId !== null && <ChatDrawer threadId={threadId} open={Boolean(threadId)} onClose={closeChat} />}
      <Popover
        open={Boolean(showTimeline)}
        anchorEl={anchorEl}
        onClose={() => setShowTimeline(undefined)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MailStateTimeline timeline={showTimeline ?? []} getStateString={getStateText} getStateIsError={getStateIsError} />
      </Popover>
    </>
  )
}

const useColumns = (
  t: TFunction<'translation', undefined>,
  getStateString: (state: EmailState) => string,
  openMailPreview: (mailTemplate: string) => void,
  openMailTimeline: (event: React.MouseEvent<HTMLDivElement>, timeline: EmailStateTransaction[]) => void,
  openChat: (threadId: string) => void,
) => {
  const formatDate = useFormatDate()
  const getStateChip = (state: EmailState, timeline: EmailStateTransaction[]) => {
    let chipConfig = {
      color: 'var(--Diamond)',
      textColor: 'var(--Cadet)',
      label: '',
      icon: faList,
    }

    switch (state) {
      case EmailState.BOUNCE:
        chipConfig = {
          color: 'var(--warning)',
          textColor: 'white',
          label: getStateString(state),
          icon: faWarning,
        }
        break
      case EmailState.CLICK:
        chipConfig = {
          color: 'var(--success)',
          textColor: 'white',
          label: getStateString(state),
          icon: faList,
        }
        break
      case EmailState.DELIVERY:
        chipConfig = {
          color: 'var(--success)',
          textColor: 'white',
          label: getStateString(state),
          icon: faList,
        }
        break

      case EmailState.ERROR:
        chipConfig = {
          color: 'var(--warning)',
          textColor: 'white',
          label: getStateString(state),
          icon: faWarning,
        }
        break

      case EmailState.OPEN:
        chipConfig = {
          color: 'var(--success)',
          textColor: 'white',
          label: getStateString(state),
          icon: faList,
        }
        break
      case EmailState.SEND:
        chipConfig = {
          color: 'var(--success)',
          textColor: 'white',
          label: getStateString(state),
          icon: faList,
        }
        break
    }
    return (
      <Chip
        onClick={(event) => openMailTimeline(event, timeline)}
        sx={{
          '& .MuiChip-label': {
            color: `${chipConfig.textColor} !important`,
          },
          backgroundColor: `${chipConfig.color} !important`,
          '& .MuiChip-icon': {
            color: `${chipConfig.textColor} !important`,
          },
        }}
        label={chipConfig.label}
        icon={<FontAwesomeIcon icon={chipConfig.icon} />}
      />
    )
  }

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: t('pms.mail_log_drawer.header_date'),
      sortable: false,
      type: 'date',
      valueFormatter: (data) => formatDate(data.value),
      maxWidth: 120,
    },
    {
      field: 'description',
      headerName: t('pms.mail_log_drawer.header_description'),
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        return (
          <Tooltip title={params.value ?? ''}>
            <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <Typography variant="body">{params.value}</Typography>
            </div>
          </Tooltip>
        )
      },
    },
    {
      field: 'receiver',
      headerName: t('pms.mail_log_drawer.header_receiver'),
      sortable: false,
      width: 250,
    },
    {
      field: 'state',
      headerName: t('pms.mail_log_drawer.header_state'),
      sortable: false,
      flex: 1,
      maxWidth: 150,
      renderCell: (params) => {
        const row = params.row as MailLogRow | ThreadRow

        if (isRowEmail(row)) {
          return (
            <Tooltip title={t('pms.mail_log_drawer.timeline')}>
              <div>{getStateChip(params.value, row.timeline)}</div>
            </Tooltip>
          )
        }

        if (isRowThread(row)) {
          return <Typography variant="body">{row.archived ? t('pms.mail_log_drawer.archived') : t('pms.mail_log_drawer.in_box')}</Typography>
        }

        return null
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('commons.table_actions'),
      getActions: (params) => {
        const row = params.row as MailLogRow | ThreadRow
        return [
          <Action
            icon={faEye}
            label={isRowEmail(row) ? t('pms.mail_log_drawer.mail_preview_tooltip') : t('pms.mail_log.see_thread')}
            showLabelInTooltip={true}
            onClick={() => {
              if (isRowEmail(row)) {
                openMailPreview(row.template)
              }
              if (isRowThread(row)) {
                openChat(row.id)
              }
            }}
          />,
        ]
      },
    },
  ]
  return columns
}
